import { Injectable, Injector } from '@angular/core';
import { CustomerService } from './customer.service';
import { Router } from '@angular/router';
import { KitchenTunnelData } from '../models/configurators-2d/applications/kitchen/kitchen-tunnel-data';
import { WorkbenchTunnelData } from '../models/configurators-2d/applications/workbench/workbench-tunnel-data';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { ProjectService } from './project.service';
import { getAppUrl } from './helpers/app-context-helper';
import { WorkbenchService } from './configurators-2d/applications/workbench.service';
import { ResumeDto } from '../models/configurators-2d/applications/workbench/resume-dto';
import { BehaviorSubject } from 'rxjs';
import { HBMMedia, HBMMedias } from '../models/configurators-2d/applications/kitchen/hbm-media';
import { StorageTunnelData } from '../models/configurators-2d/applications/storage/storage-tunnel-data';
import { logError } from './helpers/sentry.helper';
import { BathroomTunnelData } from '../models/configurators-2d/applications/bathroom/bathroom-tunnel-data';

@Injectable({
  providedIn: 'root',
})
export class TunnelService {
  public kitchenTunnelData: KitchenTunnelData;
  public workbenchTunnelData: WorkbenchTunnelData;
  public storageTunnelData: StorageTunnelData;
  public bathroomTunnelData: BathroomTunnelData;

  private isDataLoadingSubject = new BehaviorSubject<boolean>(true);
  public isDataLoading$ = this.isDataLoadingSubject.asObservable();

  private _currentContext: string;
  private _baseContext: string;
  public onEndRoute: string[];

  get isTunnel() {
    return this._currentContext != this._baseContext;
  }

  get currentContext() {
    return this._currentContext;
  }

  get baseContext() {
    return this._baseContext;
  }

  constructor(private injector: Injector) {
    this.init(this.injector.get(CustomerService).context);
  }

  setIsDataLoading(value: boolean): void {
    this.isDataLoadingSubject.next(value); // Émet une nouvelle valeur
  }

  initTunnelData() {
    if (this._baseContext == CONFIG_CONTEXT.KITCHEN) {
      this.kitchenTunnelData = new KitchenTunnelData();
      this.workbenchTunnelData = new WorkbenchTunnelData();
    } else if (this._baseContext == CONFIG_CONTEXT.STORAGE) {
      this.storageTunnelData = new StorageTunnelData();
    } else if (this._baseContext == CONFIG_CONTEXT.BATHROOM_V2) {
      this.bathroomTunnelData = new BathroomTunnelData();
    }
  }

  returnToBaseContext() {
    this._currentContext = this._baseContext;
    this.injector.get(CustomerService).context = this._baseContext;
  }

  changeContext(newContext: string, onBeginRoute: string[], onEndRoute: string[]) {
    if (!onEndRoute) {
      throw Error('No end route defined');
    }

    this._currentContext = newContext;
    this.onEndRoute = onEndRoute;
    this.injector.get(CustomerService).context = this._currentContext;
    //pas top
    switch (this._currentContext) {
      case CONFIG_CONTEXT.WORKBENCH:
        if (this.workbenchTunnelData.id) {
          this.injector
            .get(Router)
            .navigate([getAppUrl(CONFIG_CONTEXT.WORKBENCH), 'open', this.workbenchTunnelData.id]);
          this.injector.get(WorkbenchService).configuration.id = this.workbenchTunnelData.id;
          break;
        }

      default:
        this.injector.get(Router).navigate(onBeginRoute);
    }
  }

  navigateToEndRoute() {
    this.injector.get(Router).navigate(this.onEndRoute);
  }

  //A bouger dans un KitchenTunnelDataService ?
  storeKitchenConfigValue(config: any) {
    this.kitchenTunnelData.initFromConfig(config);
    this.injector
      .get(WorkbenchService)
      .getQuotationForKitchen(this.workbenchTunnelData.id)
      .then(quot => {
        this.kitchenTunnelData.quotation = quot;
      });
  }

  loadStorageTunnelData(config: any) {
    this.storageTunnelData.initFromConfig(config);
  }

  loadBathroomTunnelData(config: any) {
    this.bathroomTunnelData.initFromConfig(config);
  }

  //A bouger dans un WorkbenchTunnelDataService ?
  loadWorkbenchTunnelDataResume() {
    //J'ai volontairement laissé le traitement en promise pour ne pas passer l'intégralité de la pile d'appel en async/await par manque de temps
    this.setIsDataLoading(true);
    this.checkForWorkbenchTunnelId()
      .then(() => {
        this.injector
          .get(WorkbenchService)
          .getResumeById<ResumeDto>(this.workbenchTunnelData.id)
          .then((response: ResumeDto) => {
            this.workbenchTunnelData.resume = response;
            this.setIsDataLoading(false);
          });
      })
      .catch(() => {
        this.setIsDataLoading(false);
      });
  }

  private async checkForWorkbenchTunnelId() {
    const workbenchService = this.injector.get(WorkbenchService);
    await workbenchService.checkForTunnelId(
      this.workbenchTunnelData.id,
      this.kitchenTunnelData.HBMProjectUUID,
      CONFIG_CONTEXT.KITCHEN
    );
  }

  async getKitchenMedias(): Promise<HBMMedias> {
    let kitchenMedias: HBMMedias = new HBMMedias();
    try {
      let projectData: any = await this.injector
        .get(ProjectService)
        .getDetails(this.injector.get(TunnelService).kitchenTunnelData.HBMProjectUUID);
      projectData.medias.thumbnail.map(thumbnail => {
        let p: HBMMedia = {
          name: thumbnail.name,
          url: thumbnail.url,
        };
        kitchenMedias.thumbnail.push(p);
      });

      projectData.medias.plan2D.map(plan2D => {
        let p: HBMMedia = {
          name: plan2D.name,
          url: plan2D.url,
        };
        kitchenMedias.plan2D.push(p);
      });
    } catch (error) {
      logError(error);
    }
    return kitchenMedias;
  }

  /**
   * Fonction de mise à jour de preview
   * @returns Promise
   */
  uploadPreview(formFile) {
    this.injector.get(WorkbenchService).uploadPreview(formFile);
  }

  init(context: string) {
    this._baseContext = context;
    this._currentContext = this._baseContext;
    this.setIsDataLoading(false);

    this.initTunnelData();
  }

  reset() {
    this.injector.get(CustomerService).context = this._baseContext;
    this.init(this.injector.get(CustomerService).context);
  }

  resetFidelityCardPoint() {
    // On réinitialise la liste à chaque refresh (utile si on applique ou supprime une remise).
    this.kitchenTunnelData.fidelityCardPoint = [];
  }
}
